import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { Layout } from "@/components/layout";
import { ProtectedRoute } from "@/components/auth";
import api from "@/services/api";
import { CompletedItem, CompletedFormsResponse, FormReviewData } from "@/types";
import { PrintableConsultation } from "@/components/travelers/print/PrintableConsultation";
import { PrintableMedicationPrescription } from "@/components/travelers/print/PrintableMedicationPrescription";
import { Destination } from "@/types";

export default function Approved() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Search/filter state - initialized from URL params
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [dateFrom, setDateFrom] = useState(searchParams.get("dateFrom") || "");
  const [dateTo, setDateTo] = useState(searchParams.get("dateTo") || "");
  const [destination, setDestination] = useState(searchParams.get("destination") || "");

  // Pagination state
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1", 10));
  const pageSize = 20;
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Data state
  const [forms, setForms] = useState<CompletedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // PDF print state
  const printRef = useRef<HTMLDivElement>(null);
  const medicationPrintRef = useRef<HTMLDivElement>(null);
  const [printFormData, setPrintFormData] = useState<FormReviewData | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [pdfDropdownOpen, setPdfDropdownOpen] = useState<string | null>(null);

  const printPageStyle = `
    @page {
      size: auto;
      margin: 20mm;
    }
    html, body {
      width: 100% !important;
      height: auto !important;
      margin: 0 !important;
      padding: 0 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  `;

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: printFormData
      ? `${printFormData.consultationNumber}_${printFormData.travelerName}_Form`
      : "Consultation_Form",
    pageStyle: printPageStyle,
  });

  const handlePrintMedications = useReactToPrint({
    contentRef: medicationPrintRef,
    documentTitle: printFormData
      ? `${printFormData.consultationNumber}_${printFormData.travelerName}_Prescription`
      : "Prescription",
    pageStyle: printPageStyle,
  });

  // Close PDF dropdown when clicking outside
  useEffect(() => {
    if (!pdfDropdownOpen) return;
    const handleClickOutside = () => setPdfDropdownOpen(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [pdfDropdownOpen]);

  const fetchCompletedForms = useCallback(async (currentPage: number) => {
    try {
      setLoading(true);
      setError("");

      const currentPharmacyId = localStorage.getItem("currentPharmacyId");
      const params: Record<string, string> = {
        page: String(currentPage),
        pageSize: String(pageSize),
      };

      if (currentPharmacyId) params.pharmacy_id = currentPharmacyId;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (destination.trim()) params.destination = destination.trim();

      const response = await api.get<CompletedFormsResponse>("/pharmacy/forms/completed", { params });
      const data = response.data;

      setForms(data.forms || []);
      setTotalItems(data.totalItems || 0);
      setTotalPages(data.totalPages || 0);
      setPage(data.currentPage || 1);
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError("Authentication failed");
        return;
      }
      setError("Failed to load completed forms");
    } finally {
      setLoading(false);
    }
  }, [searchQuery, dateFrom, dateTo, destination]);

  // Initial fetch and page changes
  useEffect(() => {
    fetchCompletedForms(page);
  }, [page]);

  // Sync filters to URL
  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchQuery) params.search = searchQuery;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (destination) params.destination = destination;
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [searchQuery, dateFrom, dateTo, destination, page, setSearchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchCompletedForms(1);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
    setDestination("");
    setPage(1);
    // Fetch with no filters
    setTimeout(() => fetchCompletedForms(1), 0);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleDownloadPdf = async (travelerId: string, type: "consultation" | "prescription") => {
    try {
      setDownloadingId(travelerId);
      setPdfDropdownOpen(null);
      const response = await api.get(`/pharmacy/travelers/${travelerId}/form`);
      setPrintFormData(response.data);
      // Wait for render then trigger print
      setTimeout(() => {
        if (type === "prescription") {
          handlePrintMedications();
        } else {
          handlePrint();
        }
        setDownloadingId(null);
      }, 300);
    } catch (err) {
      console.error("Failed to load form for PDF:", err);
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDestinations = (destinations: string | Destination[]) => {
    if (typeof destinations === "string") return destinations;
    if (!Array.isArray(destinations) || destinations.length === 0) return "N/A";
    return destinations
      .sort((a, b) => a.order - b.order)
      .map((d) => {
        let label = d.destination_country;
        if (d.destination_city) label = `${d.destination_city}, ${label}`;
        if (d.days_in_location) label += ` (${d.days_in_location} days)`;
        return label;
      })
      .join(" → ");
  };

  const hasActiveFilters = searchQuery || dateFrom || dateTo || destination;

  // Pagination window
  const getPageNumbers = () => {
    const pages: number[] = [];
    const windowSize = 5;
    let start = Math.max(1, page - Math.floor(windowSize / 2));
    const end = Math.min(totalPages, start + windowSize - 1);
    if (end - start + 1 < windowSize) {
      start = Math.max(1, end - windowSize + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <ProtectedRoute>
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 py-8">
          {/* Header */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
            <div className="bg-gradient-to-r from-teal-600 to-blue-600 rounded-2xl p-8 shadow-lg text-white">
              <h1 className="text-3xl font-bold mb-2">
                Approved Consultations
              </h1>
              <p className="text-teal-100 text-lg">
                Search and download completed patient consultations
              </p>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
            <form onSubmit={handleSearch} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* Text Search */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Search
                  </label>
                  <input
                    type="text"
                    placeholder="Name or consultation #..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-gray-900 placeholder-gray-400"
                  />
                </div>

                {/* Date From */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-gray-900"
                  />
                </div>

                {/* Date To */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    To Date
                  </label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-gray-900"
                  />
                </div>

                {/* Destination */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Destination
                  </label>
                  <input
                    type="text"
                    placeholder="Country name..."
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-gray-900 placeholder-gray-400"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors shadow-sm"
                >
                  Search
                </button>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Results */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-teal-600 mx-auto mb-4"></div>
                  <p className="text-lg text-gray-700 font-medium">
                    Loading approved forms...
                  </p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md mx-auto text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Something went wrong
                </h3>
                <p className="text-gray-600 mb-6">{error}</p>
                <button
                  onClick={() => fetchCompletedForms(page)}
                  className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : forms.length > 0 ? (
              <>
                {/* Results Summary */}
                <div className="mb-4 text-sm text-gray-600">
                  Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalItems)} of {totalItems} approved forms
                </div>

                {/* Cards */}
                <div className="space-y-4">
                  {forms.map((item, index) => (
                    <div
                      key={`${item.travelerId}-${index}`}
                      className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 relative"
                    >
                      {/* Completed Tab */}
                      <div className="absolute top-0 right-0 px-3 py-1.5 rounded-tr-2xl rounded-bl-xl font-bold text-xs bg-green-600 text-white">
                        Completed
                      </div>

                      {/* Traveler Name */}
                      <div className="flex items-center mb-4 pr-20">
                        <div className="p-2.5 bg-gray-100 rounded-full mr-3">
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {item.travelerName}
                          </h3>
                          <p className="text-sm text-gray-600 mt-0.5">
                            Consultation #{item.consultationNumber}
                          </p>
                        </div>
                      </div>

                      {/* Details */}
                      <details className="group mb-4">
                        <summary className="cursor-pointer list-none flex items-center justify-between px-4 py-2.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                          <span className="text-sm font-semibold text-gray-700">View Details</span>
                          <svg className="w-4 h-4 text-gray-600 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </summary>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-2.5 mt-2">
                          <div className="flex items-start gap-2.5">
                            <div className="p-1.5 bg-teal-100 rounded-lg">
                              <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Reviewed By</div>
                              <div className="text-sm font-semibold text-gray-900">{item.reviewedBy}</div>
                            </div>
                          </div>

                          <div className="flex items-start gap-2.5">
                            <div className="p-1.5 bg-green-100 rounded-lg">
                              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Completed Date</div>
                              <div className="text-sm font-semibold text-gray-900">{formatDate(item.completedAt)}</div>
                            </div>
                          </div>

                          {item.destinations && (
                            <div className="flex items-start gap-2.5">
                              <div className="p-1.5 bg-blue-100 rounded-lg">
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Destinations</div>
                                <div className="text-sm font-semibold text-gray-900">
                                  {formatDestinations(item.destinations!)}
                                </div>
                              </div>
                            </div>
                          )}

                          {item.departureDate && item.returnDate && (
                            <div className="flex items-start gap-2.5">
                              <div className="p-1.5 bg-purple-100 rounded-lg">
                                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Travel Dates</div>
                                <div className="text-sm font-semibold text-gray-900">
                                  {formatDateShort(item.departureDate)} - {formatDateShort(item.returnDate)}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </details>

                      {/* Action Buttons */}
                      <div className="flex gap-3">
                        <button
                          onClick={() => navigate(`/travelers/${item.travelerId}/review`)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Review Form
                        </button>
                        <div className="relative">
                          <button
                            onClick={(e) => { e.stopPropagation(); setPdfDropdownOpen(pdfDropdownOpen === item.travelerId ? null : item.travelerId); }}
                            disabled={downloadingId === item.travelerId}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {downloadingId === item.travelerId ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            )}
                            PDF
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {pdfDropdownOpen === item.travelerId && (
                            <div className="absolute right-0 bottom-full mb-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                              <button
                                onClick={() => handleDownloadPdf(item.travelerId, "consultation")}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Patient Info
                              </button>
                              <button
                                onClick={() => handleDownloadPdf(item.travelerId, "prescription")}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                </svg>
                                Prescription
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-2">
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page <= 1}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>

                    {getPageNumbers().map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg ${
                          pageNum === page
                            ? "bg-teal-600 text-white"
                            : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}

                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page >= totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>

                    <span className="ml-4 text-sm text-gray-500">
                      Page {page} of {totalPages} &middot; {totalItems} total
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-2xl p-12 shadow-md text-center">
                {hasActiveFilters ? (
                  <>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      No Results Found
                    </h3>
                    <p className="text-gray-600 text-lg mb-6">
                      No approved forms match your search criteria
                    </p>
                    <button
                      onClick={handleClearFilters}
                      className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-lg transition-colors shadow-md"
                    >
                      Clear Filters
                    </button>
                  </>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      No Approved Consultations
                    </h3>
                    <p className="text-gray-600 text-lg">
                      There are no completed consultations at this time
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Hidden printable areas for PDF download */}
        <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
          <div ref={printRef}>
            {printFormData && <PrintableConsultation formData={printFormData} />}
          </div>
          <div ref={medicationPrintRef}>
            {printFormData && (
              <PrintableMedicationPrescription
                formData={printFormData}
                destinationInfoMap={{}}
              />
            )}
          </div>
        </div>
      </Layout>
    </ProtectedRoute>
  );
}
